import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Upload, X, Loader2, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

interface AIRateConfUploadProps {
  dispatcherFolderId?: string;
  dispatcherName?: string;
  onClose?: () => void;
  onSuccess: () => void;
  isModalOpen?: boolean; // Track if the modal is open
}

interface UploadingFile {
  file: File;
  status: 'queued' | 'uploading' | 'processing' | 'success' | 'error';
  progress: number;
  error?: string;
  loadNumber?: string;
}

export function AIRateConfUpload({ 
  dispatcherFolderId, 
  dispatcherName, 
  onSuccess,
  isModalOpen = true 
}: AIRateConfUploadProps) {
  const { user, profile } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [failedFileName, setFailedFileName] = useState<string>('');
  const queueRef = useRef<File[]>([]);
  
  // Use provided dispatcher info or get from current user
  const effectiveDispatcherName = dispatcherName || profile?.full_name || 'Unknown';
  const effectiveFolderId = dispatcherFolderId;

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const pdfFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      // Only accept PDFs
      if (file.type !== 'application/pdf') {
        errors.push(`${file.name}: Only PDF files are allowed for rate confirmations.`);
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: File too large. Maximum size is 10MB.`);
        return;
      }

      pdfFiles.push(file);
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (pdfFiles.length > 0) {
      // Add files to queue with 'queued' status
      const newUploadingFiles = pdfFiles.map(file => ({
        file,
        status: 'queued' as const,
        progress: 0,
      }));
      setUploadingFiles(prev => [...prev, ...newUploadingFiles]);
      
      // Add to queue
      queueRef.current.push(...pdfFiles);
      
      // Start processing if not already processing
      if (!isProcessing) {
        processQueue();
      }
    }
  };

  const processQueue = async () => {
    if (queueRef.current.length === 0) {
      setIsProcessing(false);
      
      // Check if all files are done (success or error)
      const allDone = uploadingFiles.every(f => f.status === 'success' || f.status === 'error');
      if (allDone) {
        const successCount = uploadingFiles.filter(f => f.status === 'success').length;
        const errorCount = uploadingFiles.filter(f => f.status === 'error').length;
        
        if (errorCount > 0) {
          toast.success(`${successCount} PDF(s) processed successfully, ${errorCount} failed.`);
        } else {
          toast.success('All PDFs processed successfully!');
        }
        
        // Call onSuccess to refresh the loads page
        onSuccess();
        
        // Clear the queue after 5 seconds
        setTimeout(() => {
          setUploadingFiles([]);
        }, 5000);
      }
      return;
    }

    setIsProcessing(true);
    const file = queueRef.current.shift()!;
    const remainingCount = queueRef.current.length;
    
    // Show toast for current file
    toast.info(`Processing ${file.name}... (${remainingCount} remaining in queue)`, 3000);
    
    await uploadAndProcessFile(file);
    
    // Process next file in queue
    processQueue();
  };

  const uploadAndProcessFile = async (file: File) => {
    let filePath: string | null = null;
    let documentData: any = null;
    
    console.log(`[${file.name}] Starting upload and processing...`);
    
    try {
      // Update status to uploading
      setUploadingFiles(prev =>
        prev.map(f => f.file === file ? { ...f, status: 'uploading', progress: 10 } : f)
      );

      // Generate unique file path with week folder structure
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)
      
      const formatDate = (date: Date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      };
      
      const weekFolder = `Week_${formatDate(weekStart)}_to_${formatDate(weekEnd)}`;
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${Date.now()}_${sanitizedName}`;
      filePath = `AI_RATE_CONFIRMATIONS/${effectiveDispatcherName}/${weekFolder}/${fileName}`;

      // Upload the original bytes untouched. Rewriting the PDF client-side
      // (pdf-lib) broke encrypted/odd broker PDFs so Document AI rejected them.
      const { error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Update progress
      setUploadingFiles(prev =>
        prev.map(f => f.file === file ? { ...f, progress: 40 } : f)
      );

      // Save document metadata to database
      const { data: docData, error: dbError } = await supabase
        .from('documents')
        .insert({
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: 'pdf',
          mime_type: file.type,
          folder_id: effectiveFolderId || null,
          uploaded_by: user?.id,
          ai_processed: false,
        })
        .select()
        .single();

      if (dbError) throw dbError;
      documentData = docData;

      // Update status to processing
      setUploadingFiles(prev =>
        prev.map(f => f.file === file ? { ...f, status: 'processing', progress: 60 } : f)
      );

      // Call Edge Function to process PDF and create load
      let processResult;
      let processError;
      let duplicateData = null;
      
      try {
        const response = await supabase.functions.invoke(
          'process-rate-confirmation',
          {
            body: {
              documentId: documentData.id,
              filePath: filePath,
              dispatcherName: effectiveDispatcherName,
              userId: user?.id,
            },
          }
        );
        
        processResult = response.data;
        processError = response.error;
        
        // Log what we got back from Edge Function
        console.log(`[${file.name}] Edge Function Response:`, processResult);
        console.log(`[${file.name}] Edge Function Error:`, processError);
        
        // For non-2xx responses, manually fetch the response body
        if (processError && !processResult) {
          // Try to get the actual response from the error
          // Supabase wraps the fetch, so we need to make a direct call to get the body
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const directResponse = await fetch(
            `${supabaseUrl}/functions/v1/process-rate-confirmation`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              },
              body: JSON.stringify({
                documentId: documentData.id,
                filePath: filePath,
                dispatcherName: effectiveDispatcherName,
                userId: user?.id,
              }),
            }
          );
          
          duplicateData = await directResponse.json();
          console.log(`[${file.name}] Direct Response Data:`, duplicateData);
        } else {
          duplicateData = processResult;
        }
      } catch (err) {
        console.error(`[${file.name}] Fetch error:`, err);
        throw err;
      }
      
      // FIRST: Check for duplicate load (409 status) - this is not an error!
      if (duplicateData?.duplicate) {
        // Try to get load number from response, or extract from error message
        const loadNumber = duplicateData.existingLoadNumber || 
                          duplicateData.error?.match(/#(\S+)/)?.[1] || 
                          'Unknown';
        const loadStatus = duplicateData.existingLoadStatus || 'unknown';
        
        console.log(`[${file.name}] Duplicate detected: Load #${loadNumber} (Status: ${loadStatus})`);
        
        toast.info(
          `📋 ${file.name}: Load #${loadNumber} already exists (Status: ${loadStatus}). Skipping...`, 
          6000
        );
        
        setUploadingFiles(prev =>
          prev.map(f => 
            f.file === file 
              ? { 
                  ...f, 
                  status: 'success', 
                  progress: 100, 
                  loadNumber: `#${loadNumber} (Already exists)` 
                } 
              : f
          )
        );
        
        // Clean up uploaded file for duplicates
        if (filePath) {
          try {
            await supabase.storage.from('company-documents').remove([filePath]);
          } catch (deleteError) {
            console.error(`[${file.name}] Failed to delete file:`, deleteError);
          }
        }
        
        if (documentData?.id) {
          try {
            await supabase.from('documents').delete().eq('id', documentData.id);
          } catch (deleteError) {
            console.error(`[${file.name}] Failed to delete document record:`, deleteError);
          }
        }
        
        return; // Exit early - duplicate is handled successfully
      }
      
      // SECOND: Check for actual errors (but not if it's a duplicate)
      if (processError && !processResult && !duplicateData?.duplicate) {
        // Extract detailed error message - check duplicateData first as it has the actual error
        let errorText = 'Failed to process PDF';
        
        // Try to get error from duplicateData (from direct fetch)
        if (duplicateData?.error) {
          errorText = duplicateData.error;
          
          // Special handling for PDF repair needed
          if (errorText.includes('PDF_NEEDS_REPAIR')) {
            setFailedFileName(file.name);
            setShowRepairModal(true);
            
            setUploadingFiles(prev =>
              prev.map(f => 
                f.file === file 
                  ? { ...f, status: 'error', progress: 0, error: 'PDF needs repair' } 
                  : f
              )
            );
            
            // Clean up uploaded file
            if (filePath) {
              try {
                await supabase.storage.from('company-documents').remove([filePath]);
              } catch (deleteError) {
                console.error(`[${file.name}] Failed to delete file:`, deleteError);
              }
            }
            
            if (documentData?.id) {
              try {
                await supabase.from('documents').delete().eq('id', documentData.id);
              } catch (deleteError) {
                console.error(`[${file.name}] Failed to delete document record:`, deleteError);
              }
            }
            
            return; // Exit early - user needs to repair PDF
          }
          
          // Special handling for locked/password-protected PDF
          if (errorText.includes('PDF_LOCKED')) {
            setFailedFileName(file.name);
            setShowUnlockModal(true);
            
            setUploadingFiles(prev =>
              prev.map(f => 
                f.file === file 
                  ? { ...f, status: 'error', progress: 0, error: 'PDF is locked' } 
                  : f
              )
            );
            
            // Clean up uploaded file
            if (filePath) {
              try {
                await supabase.storage.from('company-documents').remove([filePath]);
              } catch (deleteError) {
                console.error(`[${file.name}] Failed to delete file:`, deleteError);
              }
            }
            
            if (documentData?.id) {
              try {
                await supabase.from('documents').delete().eq('id', documentData.id);
              } catch (deleteError) {
                console.error(`[${file.name}] Failed to delete document record:`, deleteError);
              }
            }
            
            return; // Exit early - user needs to unlock PDF
          }
        } else if (processError.message) {
          errorText = processError.message;
        }
        
        // Log full error for debugging
        console.error(`[${file.name}] Full error object:`, JSON.stringify(processError, null, 2));
        console.error(`[${file.name}] Duplicate data:`, JSON.stringify(duplicateData, null, 2));
        
        throw new Error(errorText);
      }
      
      // Check if duplicateData contains an error (even with 200 status)
      if (duplicateData?.error && !duplicateData?.duplicate) {
        const errorText = duplicateData.error;
        const errorDetails = duplicateData.details || '';
        const errorType = duplicateData.type || 'Error';
        
        console.error(`[${file.name}] Edge Function Error:`, errorType, errorText);
        console.error(`[${file.name}] Error Details:`, errorDetails);
        
        // Special handling for PDF repair needed
        if (errorText.includes('PDF_NEEDS_REPAIR')) {
          setFailedFileName(file.name);
          setShowRepairModal(true);
          
          setUploadingFiles(prev =>
            prev.map(f => 
              f.file === file 
                ? { ...f, status: 'error', progress: 0, error: 'PDF needs repair' } 
                : f
            )
          );
          
          // Clean up uploaded file
          if (filePath) {
            try {
              await supabase.storage.from('company-documents').remove([filePath]);
            } catch (deleteError) {
              console.error(`[${file.name}] Failed to delete file:`, deleteError);
            }
          }
          
          if (documentData?.id) {
            try {
              await supabase.from('documents').delete().eq('id', documentData.id);
            } catch (deleteError) {
              console.error(`[${file.name}] Failed to delete document record:`, deleteError);
            }
          }
          
          return; // Exit early - user needs to repair PDF
        }
        
        // Special handling for locked/password-protected PDF
        if (errorText.includes('PDF_LOCKED')) {
          setFailedFileName(file.name);
          setShowUnlockModal(true);
          
          setUploadingFiles(prev =>
            prev.map(f => 
              f.file === file 
                ? { ...f, status: 'error', progress: 0, error: 'PDF is locked' } 
                : f
            )
          );
          
          // Clean up uploaded file
          if (filePath) {
            try {
              await supabase.storage.from('company-documents').remove([filePath]);
            } catch (deleteError) {
              console.error(`[${file.name}] Failed to delete file:`, deleteError);
            }
          }
          
          if (documentData?.id) {
            try {
              await supabase.from('documents').delete().eq('id', documentData.id);
            } catch (deleteError) {
              console.error(`[${file.name}] Failed to delete document record:`, deleteError);
            }
          }
          
          return; // Exit early - user needs to repair PDF
        }
        
        throw new Error(`${errorType}: ${errorText}`);
      }

      // Edge Function already updated the document, just update status to success
      const finalData = duplicateData || processResult;
      console.log(`[${file.name}] Success! Load #${finalData.loadNumber} created`);
      toast.success(`Load #${finalData.loadNumber} created from ${file.name}`);
      setUploadingFiles(prev =>
        prev.map(f => 
          f.file === file 
            ? { ...f, status: 'success', progress: 100, loadNumber: finalData.loadNumber } 
            : f
        )
      );

      // Don't call onSuccess here - wait until all files are done
      // onSuccess will be called in processQueue when queue is empty

    } catch (error: any) {
      console.error('Error uploading and processing file:', error);
      
      // Clean up: Delete the uploaded file and document record if processing failed
      if (filePath) {
        try {
          await supabase.storage.from('company-documents').remove([filePath]);
        } catch (deleteError) {
          console.error('Failed to delete file:', deleteError);
        }
      }
      
      if (documentData?.id) {
        try {
          await supabase.from('documents').delete().eq('id', documentData.id);
        } catch (deleteError) {
          console.error('Failed to delete document record:', deleteError);
        }
      }
      
      setUploadingFiles(prev =>
        prev.map(f => 
          f.file === file 
            ? { ...f, status: 'error', error: error.message || 'Failed to process file' } 
            : f
        )
      );
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeUploadingFile = (file: File) => {
    setUploadingFiles(prev => prev.filter(f => f.file !== file));
  };

  return (
    <>
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-orange-900 mb-1">How it works:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Upload rate confirmation PDF files</li>
              <li>AI will automatically extract load details</li>
              <li>A new load will be created automatically</li>
              <li>The document will be attached to the load</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
          isDragging
            ? 'border-orange-500 bg-orange-50'
            : 'border-slate-300 hover:border-orange-500 hover:bg-orange-50'
        }`}
      >
            <div className="flex flex-col items-center">
              <div className="p-4 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full mb-4">
                <Upload className="w-8 h-8 text-orange-600" />
              </div>
              <p className="text-lg font-semibold text-slate-700 mb-2">
                Drag & Drop Rate Confirmation PDFs
              </p>
              <p className="text-sm text-slate-500 mb-2">or click to browse</p>
              <p className="text-xs text-slate-400">
                PDF files only (Max 10MB per file)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,application/pdf"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
          </div>

          {/* Uploading Files */}
          {uploadingFiles.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">Processing Files</h4>
              {uploadingFiles.map((uploadingFile, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200"
                >
                  {/* Icon based on status */}
                  {uploadingFile.status === 'uploading' && (
                    <Upload className="w-8 h-8 text-blue-600 flex-shrink-0 animate-pulse" />
                  )}
                  {uploadingFile.status === 'processing' && (
                    <Loader2 className="w-8 h-8 text-purple-600 flex-shrink-0 animate-spin" />
                  )}
                  {uploadingFile.status === 'success' && (
                    <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                  )}
                  {uploadingFile.status === 'error' && (
                    <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {uploadingFile.file.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {(uploadingFile.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>

                    {/* Status Text */}
                    {uploadingFile.status === 'uploading' && (
                      <p className="text-xs text-blue-600 mt-1">Uploading...</p>
                    )}
                    {uploadingFile.status === 'processing' && (
                      <p className="text-xs text-purple-600 mt-1">AI is processing...</p>
                    )}
                    {uploadingFile.status === 'success' && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Load created: {uploadingFile.loadNumber}
                      </p>
                    )}
                    {uploadingFile.status === 'error' && (
                      <p className="text-xs text-red-600 mt-1">{uploadingFile.error}</p>
                    )}

                    {/* Progress Bar */}
                    {uploadingFile.status !== 'error' && uploadingFile.status !== 'success' && (
                      <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
                        <div
                          className="bg-gradient-to-r from-purple-600 to-blue-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadingFile.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>

                  {/* Remove button (only for errors) */}
                  {uploadingFile.status === 'error' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeUploadingFile(uploadingFile.file);
                      }}
                      className="p-1 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
                    >
                      <X className="w-5 h-5 text-slate-600" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Fixed Queue Display - Bottom Left Corner - Only visible when modal is closed */}
      {uploadingFiles.length > 0 && !isModalOpen && createPortal(
        <div className="fixed bottom-4 left-4 z-[9999] w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-2xl border border-slate-200">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing Queue ({uploadingFiles.filter(f => f.status !== 'success' && f.status !== 'error').length} active)
            </h3>
            <span className="text-xs opacity-90">{uploadingFiles.length} total</span>
          </div>
        </div>
        
        <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
          {uploadingFiles.map((file, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 p-2 rounded-lg border ${
                file.status === 'queued' ? 'bg-slate-50 border-slate-200' :
                file.status === 'uploading' ? 'bg-blue-50 border-blue-200' :
                file.status === 'processing' ? 'bg-purple-50 border-purple-200' :
                file.status === 'success' ? 'bg-green-50 border-green-200' :
                'bg-red-50 border-red-200'
              }`}
            >
              {/* Status Icon */}
              {file.status === 'queued' && (
                <div className="w-5 h-5 rounded-full bg-slate-300 flex items-center justify-center text-xs text-white font-bold">
                  {index + 1}
                </div>
              )}
              {file.status === 'uploading' && (
                <Upload className="w-5 h-5 text-blue-600 animate-pulse" />
              )}
              {file.status === 'processing' && (
                <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
              )}
              {file.status === 'success' && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              {file.status === 'error' && (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              
              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-900 truncate">{file.file.name}</p>
                <p className="text-[10px] text-slate-500">
                  {file.status === 'queued' && 'Waiting in queue...'}
                  {file.status === 'uploading' && 'Uploading...'}
                  {file.status === 'processing' && 'AI Processing...'}
                  {file.status === 'success' && `✓ ${file.loadNumber}`}
                  {file.status === 'error' && file.error}
                </p>
              </div>
            </div>
          ))}
        </div>
        </div>,
        document.body
      )}

      {/* PDF Repair Modal */}
      {showUnlockModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-bold text-white">PDF Unlock Required</h3>
                </div>
                <button
                  onClick={() => setShowUnlockModal(false)}
                  className="text-white hover:text-slate-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold text-yellow-900">File:</span>{' '}
                  <span className="font-mono text-sm">{failedFileName}</span>
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-slate-700">
                  This PDF file is password-protected or locked. Our system cannot read protected PDFs.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">🔓 How to unlock:</p>
                  <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
                    <li>Click the button below to open the PDF unlock tool</li>
                    <li>Upload your locked PDF file</li>
                    <li>Download the unlocked PDF</li>
                    <li>Upload the unlocked PDF here again</li>
                  </ol>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <a
                  href="https://www.ilovepdf.com/unlock_pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-3 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl text-center"
                >
                  🔓 Unlock PDF Online
                </a>
                <button
                  onClick={() => setShowUnlockModal(false)}
                  className="px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                The unlock tool is free and opens in a new tab
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showRepairModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-bold text-white">PDF Repair Required</h3>
                </div>
                <button
                  onClick={() => setShowRepairModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold text-orange-900">File:</span>{' '}
                  <span className="font-mono text-sm">{failedFileName}</span>
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-slate-700">
                  The PDF file has formatting issues that prevent our system from reading it. 
                  This can happen with certain PDF generators or corrupted files.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">📝 How to fix:</p>
                  <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
                    <li>Click the button below to open the PDF repair tool</li>
                    <li>Upload your PDF file to repair it</li>
                    <li>Download the repaired PDF</li>
                    <li>Upload the repaired PDF here again</li>
                  </ol>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <a
                  href="https://www.ilovepdf.com/repair-pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all shadow-lg hover:shadow-xl text-center"
                >
                  🔧 Repair PDF Online
                </a>
                <button
                  onClick={() => setShowRepairModal(false)}
                  className="px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                The repair tool is free and opens in a new tab
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
      </>
    );
  }
